import {useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {RootStore} from "../configureStore";
import {SignUpActions, UserLevels} from "../containers/RegistrationLoginPage/types";
import {
    setBachelorMasterData,
    setCourseLevel,
    setCourseLevelAndLoadingData,
    setSpecializationVisibilityAction,
    setExamStatusDependentVisibility,
} from "../containers/RegistrationLoginPage/actions";
import {useForm} from "react-final-form";
import {getFieldsByLevel} from "../containers/RegistrationLoginPage/utils";
import {ThunkDispatch} from "redux-thunk";
import {isMMPType} from "../components/CTA/response";
const useManageSpecializationField = () => {
    const defaultPreferredSpecialization = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.defaultPreferredSpecialization,
    );
    const initialFormLevel = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.signUpPageData?.formLevel,
    );
    return useCallback(
        (
            dispatch: ThunkDispatch<RootStore, undefined, SignUpActions>,
            intakeYear?: string,
            level?: UserLevels,
        ) => {
            if (!intakeYear) {
                if (level && level === initialFormLevel) {
                    dispatch(
                        setSpecializationVisibilityAction({
                            defaultCase: defaultPreferredSpecialization,
                        }),
                    );
                } else {
                    dispatch(
                        setSpecializationVisibilityAction({
                            visible: false,
                        }),
                    );
                }
            }
        },
        [initialFormLevel, defaultPreferredSpecialization],
    );
};
type useLevelHandlerReturnType = ({
    level,
    courseId,
    intakeYear,
}: {
    level: UserLevels;
    courseId?: number | undefined;
    intakeYear?: string | undefined;
}) => Promise<void>;

const useLevelHandler = (): useLevelHandlerReturnType => {
    const dispatch = useDispatch<ThunkDispatch<RootStore, undefined, SignUpActions>>();
    const masterFields = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.masterFields,
    );
    const bachelorFields = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.bachelorFields,
    );
    const phdFields = useSelector((state: RootStore) => state.RegistrationLoginPage.phdFields);
    const universityId = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.universityId,
    );
    const trackingKey = useSelector((state: RootStore) => state.RegistrationLoginPage.trackingKey);
    const specializationPromiseCancel = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.specializationPromiseCancel,
    );
    const ctaTrackingData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.ctaTrackingData,
    );
    const manageSpecializationField = useManageSpecializationField();
    const form = useForm();
    return useCallback(
        async ({
            level,
            courseId,
            intakeYear,
        }: {
            level: UserLevels;
            courseId?: number;
            intakeYear?: string;
        }) => {
            // hide passport and exam score field
            dispatch(
                setExamStatusDependentVisibility({
                    passport: false,
                    examScore: false,
                    level: level,
                }),
            );
            if (specializationPromiseCancel) {
                specializationPromiseCancel();
            }
            manageSpecializationField(dispatch, intakeYear);
            const currentLevelOpposite = level === "Bachelors" ? "Masters" : "Bachelors";
            let fieldToReset = getFieldsByLevel(currentLevelOpposite, !!intakeYear);
            if (!intakeYear) {
                fieldToReset = [...fieldToReset, "preferredSpecializationV1"];
            }
            // unset exam status and dependent fields
            fieldToReset.push("examStatus", "examScore", "passport");
            const registeredFields = form.getRegisteredFields();
            registeredFields.map((registeredField) => {
                fieldToReset.map((field) => {
                    if (registeredField.includes(field)) {
                        form.resetFieldState(registeredField);
                        form.change(registeredField, undefined);
                    }
                });
            });
            if (
                (level === "Bachelors" && !bachelorFields) ||
                (level === "Masters" && !masterFields) ||
                (level === "PhD" && !phdFields)
            ) {
                dispatch(setCourseLevelAndLoadingData(level, !!intakeYear));
                if (trackingKey) {
                    const fetchPromise = dispatch(
                        setBachelorMasterData({
                            level: level,
                            trackingId: trackingKey,
                            courseId: courseId,
                            universityId: universityId,
                            intakeYear: intakeYear,
                            mmpId:
                                ctaTrackingData && isMMPType(ctaTrackingData)
                                    ? ctaTrackingData.mmpId
                                    : undefined,
                            formType: "registration",
                        }),
                    );
                    await fetchPromise;
                    manageSpecializationField(dispatch, intakeYear, level);
                }
            } else {
                dispatch(setCourseLevel(level));
                manageSpecializationField(dispatch, intakeYear, level);
            }
        },
        [
            dispatch,
            masterFields,
            bachelorFields,
            trackingKey,
            form,
            specializationPromiseCancel,
            phdFields,
            universityId,
            manageSpecializationField,
            ctaTrackingData,
        ],
    );
};
export default useLevelHandler;
